import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

/**
 * Validator for IBAN bank account numbers.
 * Supports international IBANs and also allows Polish account numbers without the "PL" prefix.
 * Accepts numbers with or without spaces.
 */

export const ibanErrorName = 'invalidIban';
export function ibanValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value: string = control.value;

    // If control is empty, don't validate here (use separate required validator if needed)
    if (!value) {
      return null;
    }

    // Remove all spaces and convert to uppercase for uniform processing
    let iban = value.replace(/\s+/g, '').toUpperCase();

    // If it matches 26 digits only (polish account without "PL" prefix), prepend "PL"
    // Polish IBAN length is 28 characters: "PL" + 26 digits
    if (/^\d{26}$/.test(iban)) {
      iban = 'PL' + iban;
    }

    // Validate general IBAN format: two letters country code + 2-34 alphanumeric characters
    if (!/^[A-Z]{2}[0-9A-Z]{2,34}$/.test(iban)) {
      return { [ibanErrorName]: true };
    }

    // Move the first 4 characters (country code and check digits) to the end of the string
    const rearranged = iban.slice(4) + iban.slice(0, 4);

    // Replace letters with numbers: A=10, B=11, ... Z=35
    const converted = rearranged.replace(/[A-Z]/g, (char) =>
      (char.charCodeAt(0) - 55).toString(),
    );

    // Perform modulo 97 check in chunks to handle large numbers
    let remainder = '';
    for (const chunk of converted.match(/.{1,7}/g) || []) {
      remainder = (BigInt(remainder + chunk) % 97n).toString();
    }

    // Valid IBAN numbers have a remainder of 1
    if (parseInt(remainder, 10) !== 1) {
      return { [ibanErrorName]: true };
    }

    // Valid IBAN
    return null;
  };
}
